import { watch } from 'vue'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { RTCSessionFacade, STATE, useWebRTC } from '@/entities/webrtc'

export type IncomingAnswerStrategy =
  | 'accept'
  | 'end-and-accept'
  | 'hold-and-accept'
  | 'hold-incoming-and-accept'

export type IncomingAnswerMedia = {
  withAudio: boolean
  withVideo: boolean
}

type AnswerFn = (withAudio: boolean, withVideo: boolean) => void | Promise<void>

type RunIncomingAnswerOptions = {
  answer: AnswerFn
  strategy: IncomingAnswerStrategy
  incomingSessionId: string
  media: IncomingAnswerMedia
}

const isConnectedOrHold = (state: STATE) => (
  state === STATE.CONNECTED || state === STATE.ONHOLD
)

const waitForSessionConnectedOrHold = (
  facade: RTCSessionFacade,
  timeoutMs = 10_000,
): Promise<void> => {
  if (isConnectedOrHold(facade.sessionState.value)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      stopWatch()
      reject(new Error('Timed out waiting for session to connect'))
    }, timeoutMs)

    const stopWatch = watch(() => facade.sessionState.value, (state) => {
      if (!isConnectedOrHold(state)) {
        return
      }
      window.clearTimeout(timeoutId)
      stopWatch()
      resolve()
    })
  })
}

export const getConnectedOtherSessions = (
  incomingSessionId: string,
  sessions: Map<string, RTCSessionFacade>,
): RTCSessionFacade[] => {
  return Array.from(sessions.values()).filter((facade) => {
    return facade.sessionId !== incomingSessionId
      && facade.sessionState.value === STATE.CONNECTED
  })
}

export const resolveCurrentConnectedSession = (
  incomingSessionId: string,
  sessions: Map<string, RTCSessionFacade>,
  currentFacade?: RTCSessionFacade,
): RTCSessionFacade | undefined => {
  if (currentFacade
    && currentFacade.sessionId !== incomingSessionId
    && currentFacade.sessionState.value === STATE.CONNECTED
  ) {
    return currentFacade
  }

  return getConnectedOtherSessions(incomingSessionId, sessions)[0]
}

export const shouldShowIncomingAnswerMenu = (
  incomingSessionId: string,
  sessions: Map<string, RTCSessionFacade>,
  incomingIsGroup: boolean,
  hasConnectedGroupSession = false,
): boolean => {
  if (incomingIsGroup || hasConnectedGroupSession) {
    return false
  }

  return getConnectedOtherSessions(incomingSessionId, sessions).length > 0
}

export const runIncomingAnswerStrategy = async ({
  answer,
  strategy,
  incomingSessionId,
  media,
}: RunIncomingAnswerOptions): Promise<void> => {
  const {
    sessions,
    currentRTCSessionFacade,
    setActiveSession,
    setSkipHoldOnNextConfirmed,
    clearSkipHoldOnNextConfirmed,
    getRTCSessionFacadeById,
  } = useWebRTC()
  const sessionRoomStore = useCallSessionRoomStore()

  if (strategy === 'accept') {
    await answer(media.withAudio, media.withVideo)
    return
  }

  const connectedSession = resolveCurrentConnectedSession(
    incomingSessionId,
    sessions.value,
    currentRTCSessionFacade.value,
  )

  if (!connectedSession) {
    await answer(media.withAudio, media.withVideo)
    return
  }

  if (strategy === 'end-and-accept') {
    connectedSession.terminate()
    await answer(media.withAudio, media.withVideo)
    return
  }

  if (strategy === 'hold-and-accept') {
    const connectedRoom = sessionRoomStore.getRoom(connectedSession.sessionId)
    if (connectedRoom?.isGroup) {
      connectedSession.toggleHoldInConference()
    } else if (!connectedSession.isOnHold.value) {
      connectedSession.toggleHold()
    }
    await answer(media.withAudio, media.withVideo)
    return
  }

  if (strategy === 'hold-incoming-and-accept') {
    // answer резолвится на accepted раньше confirmed → CONNECTED.
    // confirmed без skip сделал бы hold текущего; со skip — ждём CONNECTED и сами hold входящего.
    const previousSessionId = connectedSession.sessionId
    setSkipHoldOnNextConfirmed()
    try {
      await answer(media.withAudio, media.withVideo)

      const incomingFacade = getRTCSessionFacadeById(incomingSessionId)
      if (!incomingFacade) {
        clearSkipHoldOnNextConfirmed()
        return
      }

      await waitForSessionConnectedOrHold(incomingFacade)

      if (!incomingFacade.isOnHold.value) {
        incomingFacade.toggleHold()
      }

      setActiveSession(previousSessionId, { holdOthers: false })
    } catch (error) {
      clearSkipHoldOnNextConfirmed()
      throw error
    }
  }
}
